import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Flame, Scale, Calculator, RefreshCw, Star, TrendingUp, Link2 } from 'lucide-react'
import { Seo } from '../components/Seo'

interface AnalysisMethod {
  id: string
  title: string
  description: string
  icon: any
  gradient: string
  path: string
}

const analysisMethods: AnalysisMethod[] = [
  {
    id: 'hot-cold',
    title: '핫 & 콜드 넘버',
    description: '숫자를 불꽃(Hot)과 얼음(Cold) 아이콘으로 시각화하여 특정 기간 동안의 출현 빈도를 확인하세요',
    icon: Flame,
    gradient: 'from-red-500 to-orange-500',
    path: '/analysis/hot-cold'
  },
  {
    id: 'odd-even',
    title: '홀짝 비율 통계',
    description: '역대 1등 번호의 홀짝 비율 중 가장 빈번했던 조합을 황금 밸런스로 추천합니다',
    icon: Scale,
    gradient: 'from-purple-500 to-pink-500',
    path: '/analysis/odd-even'
  },
  {
    id: 'number-sum',
    title: '숫자 총합 분석',
    description: '역대 당첨 번호 총합의 분포를 곡선 그래프로 보여주고, 가장 확률이 높은 총합 구간을 위닝 레인지로 표시합니다',
    icon: Calculator,
    gradient: 'from-blue-500 to-cyan-500',
    path: '/analysis/number-sum'
  },
  {
    id: 'reappearance',
    title: '재출현 주기 분석',
    description: '각 번호별로 마지막 출현일과 평균 재출현 주기를 계산하여 컴백 임박 신호를 제공합니다',
    icon: RefreshCw,
    gradient: 'from-green-500 to-emerald-500',
    path: '/analysis/reappearance'
  },
  {
    id: 'lucky-number',
    title: '나만의 수호신 번호',
    description: '생년월일, 별자리 등의 정보를 바탕으로 개인화된 행운의 번호를 생성합니다',
    icon: Star,
    gradient: 'from-yellow-500 to-amber-500',
    path: '/analysis/lucky-number'
  },
  {
    id: 'consecutive-numbers',
    title: '연속 번호 패턴',
    description: '역대 당첨 번호 중 연속 번호(예: 11, 12)가 포함된 비율을 통계로 보여줍니다',
    icon: Link2,
    gradient: 'from-green-500 to-emerald-500',
    path: '/analysis/consecutive-numbers'
  }
]

const AnalysisCard = ({ method, index }: { method: AnalysisMethod; index: number }) => {
  const Icon = method.icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8, scale: 1.02 }}
    >
      <Link to={method.path}>
        <div className={`relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br ${method.gradient} p-8 text-white min-h-[280px] flex flex-col justify-between`}>
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Icon className="w-10 h-10" strokeWidth={2} />
            </div>
          </div>

          {/* Content */}
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-3">{method.title}</h3>
            <p className="text-white/90 text-sm leading-relaxed">
              {method.description}
            </p>
          </div>

          {/* Hover Arrow */}
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <TrendingUp className="w-6 h-6" />
          </div>

          {/* Decorative circles */}
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full"></div>
        </div>
      </Link>
    </motion.div>
  )
}

const AnalysisHomePage = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <Seo
        title="로또 번호 분석"
        description="핫/콜드, 홀짝, 총합, 재출현 주기 등 다양한 통계 기반 분석으로 로또 번호를 더 스마트하게 추천받아보세요."
        canonicalPath="/analysis"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-happiness-primary via-purple-600 to-indigo-700 py-20 px-4">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 drop-shadow-lg">
              번호 분석
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold text-white/90 mb-4">
              AI 기반 로또 번호 분석 시스템
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-3xl mx-auto">
              다양한 분석 방법으로 당신의 행운을 찾아보세요
            </p>
          </motion.div>
        </div>

        {/* Floating elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-white/10 rounded-full animate-pulse delay-300"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-pulse delay-700"></div>
      </section>

      {/* Analysis Methods Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold bg-gradient-to-r from-happiness-primary to-purple-600 bg-clip-text text-transparent mb-4">
              분석 방법 선택
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              원하는 분석 방법을 선택하여 자세한 정보를 확인하세요
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {analysisMethods.map((method, index) => (
              <AnalysisCard key={method.id} method={method} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 px-4 bg-white dark:bg-gray-900 transition-colors">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              왜 번호 분석이 중요할까요?
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
              과거 당첨 번호 데이터를 분석하면 숫자의 출현 패턴, 빈도, 주기 등을 파악할 수 있습니다.
              물론 로또는 무작위 추첨이지만, 통계적 분석을 통해 더 현명한 선택을 할 수 있습니다.
            </p>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 dark:border-yellow-600 p-6 text-left">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong className="text-yellow-700 dark:text-yellow-400">⚠️ 주의:</strong> 이 분석은 과거 당첨 번호의 통계적 분석이며, 
                미래 당첨 번호를 예측하거나 보장하지 않습니다. 
                로또는 무작위 추첨이므로 모든 번호는 동일한 당첨 확률을 가집니다.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default AnalysisHomePage

